import { CloseOutlined, MessageOutlined } from '@ant-design/icons';
import { Button, Empty, Form, Image, MenuProps, UploadProps } from 'antd';
import { RcFile } from 'antd/es/upload';
import api from 'api';
import cn from 'classnames';
import { AttachmentModal } from 'components/Modals';
import getDateWithTimeZone from 'helpers/getDateWithTimeZone';
import { IChat, IChatMessage } from 'interfaces';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { GroupedVirtuoso, GroupedVirtuosoHandle } from 'react-virtuoso';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { fetchAllTagsDocuments, fetchChatHistory, fetchUnviewedAmount } from 'store/reducers/chatCreator';
import { setIsAttachmentModalOpen, setIsLoadingTagFile, setMessageContent, setMessageForReply } from 'store/slices/chatsSlice';
import CustomInput from './CustomInput/CustomInput';
import { CURRENT_INPUT_TAG, INPUT_TAG, MESSAGE_TAG, USER_MESSAGE_TAG } from './Messages/patterns';
import RecieveMessage from './Messages/RecieveMessage';
import SendMessage from './Messages/SendMessage';
import classes from './Messanger.module.scss';
import { ChatSettingIcon } from 'assets/icons/ChatSettingIcon';
import { setDocsSegment, setIsRSidebarCollapsed } from 'store/slices/windowStateSlice';
import Spinner from 'components/Spinner/Spinner';
import fallbackHolder from 'assets/img/fallback.png';
import { parsedChatMessages } from 'helpers/getLinkedTextArray';
import { setSelectedDocument, setViewType } from 'store/slices/dataDocumentsSlice';
import UploadingComponent from 'components/UploadingComponent/UploadingComponent';
import { getTheme } from 'helpers/getTheme';
import { setCursorToTheEnd } from 'helpers/setCursorToTheEnd';
import CustomDragger from './CustomDragger/CustomDragger';
import { useDebounce } from 'use-debounce';
import { getDeclension } from 'helpers/selectedObjects';
import AwaitingMessage from './Messages/AwaitingMessage';

type Props = {
  onSendMessage: (event: any, att?: any) => void;
  chatGroups: IChatMessage[][];
  chatInfo: IChat | null;
  embedded?: boolean;
  aiAssistant?: boolean;
};

const Messanger: React.FC<Props> = ({ onSendMessage, chatGroups, chatInfo, embedded, aiAssistant }) => {
  const [tagsDocuments, setTagsDocuments] = React.useState<MenuProps['items']>([]);
  const [attachment, setAttachment] = React.useState<File[]>([]);
  const [needRequestDocs, setNeedRequestDocs] = React.useState(true);
  const [firstItemIndex, setFirstItemIndex] = React.useState(1000);
  const [forbiddenExt, setForbiddenExt] = React.useState(false);
  const [isSendingDisabled, setIsSendingDisabled] = React.useState(false);

  const editableRef = React.useRef<HTMLDivElement>(null);
  const bodyRef = React.useRef<GroupedVirtuosoHandle>(null);
  
  const { userData } = useAppSelector((state) => state.userData);
  const { isRSidebarCollapsed, rSidebarEntity, messageApi } = useAppSelector((state) => state.windowState);
  const { rootFolderForPermissions } = useAppSelector((state) => state.permissions);
  const { dataRoom } = useAppSelector((state) => state.dataRoom);
  const { messageForReply, chatData, currnetChatLoading, allTagsDocuments, isAttachmentModalOpen,
    isLoadingTagsDocuments, isLoadingTagFile, messageContent } = useAppSelector((state) => state.chat);

  const [debouncedForbiddenExt] = useDebounce(forbiddenExt, 100);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { i18n, t } = useTranslation();
  const [form] = Form.useForm();

  const isSupportChat = chatInfo?.entity_type === 5;
  const SUPPORT_PHONE = '+7 (999) 123-45-67';
  const groupCounts = chatGroups?.map((group) => group.length);
  const flattedGroups = React.useMemo(() => chatGroups?.flat(), [chatGroups]);
  const isTagsDisabled = Boolean(rootFolderForPermissions?.permissions?.length);
  const titleNames = [t('Chats.allMessages1'), t('Chats.allMessages2'), t('Chats.allMessages3')];
  
  React.useEffect(() => {
    const lastGroup = chatGroups[chatGroups?.length - 1];
    const lastMessage = lastGroup[lastGroup?.length - 1];
    if (lastMessage?.email === userData?.email && aiAssistant) {
      setIsSendingDisabled(true);
    } else {
      setIsSendingDisabled(false);
    }
  }, [chatGroups]);

  React.useEffect(() => {
    dispatch(fetchUnviewedAmount({ data: chatInfo, count: -1 }));
    form.setFieldValue('message', '');
    
    return () => {
      dispatch(setMessageForReply(null));
      aiAssistant && dispatch(setMessageContent(''));
    };
  }, []);
  
  React.useEffect(() => {
    if (debouncedForbiddenExt) {
      messageApi?.warning(t('Chats.forbidenExt'), 8);
      setForbiddenExt(false);
    }
  }, [debouncedForbiddenExt]);

  React.useEffect(() => {
    if (dataRoom && !aiAssistant) {      
      if (!allTagsDocuments[0] && !isLoadingTagsDocuments && needRequestDocs) {
        dispatch(fetchAllTagsDocuments({room_id: dataRoom.id}));
        setNeedRequestDocs(false);
      }
    }
  }, [dataRoom, i18n.language, allTagsDocuments, isLoadingTagsDocuments]);

  React.useEffect(() => {
    if (allTagsDocuments[0]) fetchTagsDocuments();
  }, [allTagsDocuments]);

  const fetchTagsDocuments = async () => {
    try {
      setTagsDocuments(
        allTagsDocuments.map((doc) => {
          const folderName = doc.folder_name === 'Root'
            ? t('Documents.management.rootFolder')
            : doc.folder_name;
          
          return {
            key: `${doc.id} ${folderName}/${doc.name}`,
            label: `${folderName}/${doc.name}`,
          }
        })
      );
    } catch (err) {
      console.log(err);
    }
  };

  const onOpenAttachment = () => {
    dispatch(setIsAttachmentModalOpen(true));
    if (editableRef.current) {
      editableRef.current.innerHTML = '';
    }
  };

  const onCloseAttachment = (needToTransitData: boolean) => {
    setAttachment([]);
    dispatch(setIsAttachmentModalOpen(false));
    if (editableRef.current && needToTransitData) {      
      editableRef.current.innerHTML = messageContent;
      setCursorToTheEnd(editableRef.current);
    }
  };

  const openRightSideBar = () => {
    dispatch(
      setIsRSidebarCollapsed({
        collapsed: !isRSidebarCollapsed && rSidebarEntity !== 'chat_setting' ? false : !isRSidebarCollapsed,
        entity: isRSidebarCollapsed || (!isRSidebarCollapsed && rSidebarEntity !== 'chat_setting') ? 'chat_setting' : '',
        width: isRSidebarCollapsed || (!isRSidebarCollapsed && rSidebarEntity !== 'chat_setting') ? 300 : 0,
      })
    );
  };
  
  const onFinish = async () => {
    let content = messageContent;
    
    content = content.replace(
      /<span[^>]*data-type="user"[^>]*data-email="([^"]+)"[^>]*>@([^<]+)<\/span>/g,
      (match, email, name) => `${USER_MESSAGE_TAG.OPEN}${email}]${name}${USER_MESSAGE_TAG.CLOSE}`
    );
    
    content = content.replace(
      /<span[^>]*data-type="user"[^>]*data-current="true"[^>]*>@<\/span>/g,
      '@'
    );
    
    content = content
      .replaceAll(`${CURRENT_INPUT_TAG.OPEN}#${CURRENT_INPUT_TAG.CLOSE}`, '#')
      .replaceAll(`${INPUT_TAG.CLOSE}#${INPUT_TAG.CLOSE}`, '#')
      .replaceAll(INPUT_TAG.OPEN, MESSAGE_TAG.OPEN)
      .replaceAll(CURRENT_INPUT_TAG.OPEN, MESSAGE_TAG.OPEN)
      .replaceAll(INPUT_TAG.CLOSE, MESSAGE_TAG.CLOSE);

    if (attachment[0]) {
      try {
        const formData = new FormData();
        attachment?.forEach((file) => formData.append('files', file));
        const response = await api.uploadAttachment(formData);
        onSendMessage(
          content?.replace(/\<br\>/g, '\n').replace(/&nbsp;/g, ' '),
          response.data.attachments.map(({ id }) => id)
        );
      } catch (err) {
        messageApi?.error(t('Documents.modals.share.sharedError'));
      }
      onCloseAttachment(false);
    } else {
      onSendMessage(content?.replace(/\<br\>/g, '\n').replace(/&nbsp;/g, ' '));
      form.resetFields(['message']);
    }
    dispatch(setMessageContent(''));
  };

  const onTagClick = async (event: React.MouseEvent<HTMLAnchorElement>) => {
    dispatch(setIsLoadingTagFile(true));
    try {
      const splittedName = (event.target as HTMLAnchorElement).textContent?.split('/')!
      const docName = splittedName[splittedName.length - 1];
      const respone = (await api.hasDuplicate({ room_id: dataRoom?.id!, document_name: docName! }));      
      const document = respone.data[0];
      const documentWithPermissions = await api.getDocumentById(String(document?.id));
      
      dispatch(setViewType('Document'));
      dispatch(setSelectedDocument(documentWithPermissions.data));
      // dispatch(setDocsSegment('loader')); // отменил изменения в рамках задачи 21868
      dispatch(setDocsSegment('view'));
      navigate(`/room/${dataRoom!.id}/document/?modalSection=view&document_id=${document.id}`);

    } catch (err) {
      console.log(err);
    } finally {
      dispatch(setIsLoadingTagFile(false))
    }
  };

  const replyHandle = (message: IChatMessage) => {
    const replied_to = {
      id: message.id,
      content: message.message,
      author: { name: message.user_name },
      attachment_ids: message.attachment_ids,
    };
    dispatch(setMessageForReply(replied_to));
  }

  const contextMenu = (message: IChatMessage): MenuProps['items'] => [
    {
      label: (
        <span>
          <MessageOutlined style={{ paddingRight: 6, fontSize: 16 }} />
          {t('Chats.reply')}
        </span>
      ),
      key: 'reply',
      disabled: message.status === 'pending',
      onClick: () => replyHandle(message),
    },
  ];

  const scrollToElement = (chatMessage: IChatMessage) => {
    if (bodyRef.current) {
      bodyRef.current.scrollToIndex({
        index: chatData[chatInfo?.id!].data.findIndex(({ id }) => id === chatMessage.replied_to?.id!),
        align: 'start', // или "end" для прокрутки вниз
      });
    }

    setTimeout(() => {
      const targetMessage = chatMessage?.replied_to?.id && document.getElementById(chatMessage?.replied_to?.id);
      if (targetMessage && targetMessage.style) {
        targetMessage.style.setProperty('opacity', '.75');
        targetMessage.style.setProperty('transform', 'scale(1.05)');

        setTimeout(() => {
          targetMessage.style.setProperty('opacity', '1');
          targetMessage.style.setProperty('transform', 'scale(1)');
        }, 1000);
      }
    }, 10);
  };

  const replyOn = (chatMessage: IChatMessage, isRecieve: boolean) => {
    return (
      <div
        onClick={() => scrollToElement(chatMessage)}
        className={cn(classes.repliedWrap, isRecieve ? classes.recieve : classes.send)}
      >
        <div className={classes.repliedAuthor}>{chatMessage.replied_to?.author?.name}</div>
        {Boolean(chatMessage.replied_to?.attachment_ids?.length) && (
          <div className={classes.repliedImages}>
            {chatMessage.replied_to?.attachment_ids?.slice(0, 3).map((id, index) => 
              {
                return (
                  <Image
                  key={`reply-image-${index}`}
                  src={id}
                  fallback={fallbackHolder}
                  alt='Attachment'
                  className={classes.repliedImage}
                  preview={false}
                /> 
                )
              }
            )}
            {(chatMessage.replied_to?.attachment_ids?.length || 0) > 3 && (
              <div className={classes.repliedImageCount}>
                +{(chatMessage.replied_to?.attachment_ids?.length || 0) - 3}
              </div>
            )}
          </div>
        )}
        {chatMessage.replied_to?.content && (
          <div className={classes.repliedMessage}>{parsedChatMessages(
            chatMessage.replied_to?.content,
            { className: classes.messageTag, disabled: isTagsDisabled, onClick: onTagClick },
            isTagsDisabled ? t('Documents.table.noPermissionToView') : undefined,
            classes.urlLink,
            classes.userMessageTag
          )}</div>
        )}
      </div>
    );
  };

  const uploadProps: UploadProps = {
    beforeUpload: (file: RcFile, allFiles: RcFile[]) => {
      const images = allFiles.filter((f) => f.type.includes('image'));
      const anotherExt = allFiles.filter((f) => !f.type.includes('image'))
      if (anotherExt[0]) setForbiddenExt(true);
      if (images[0]) {
        setAttachment([...attachment, ...images]);
        dispatch(setIsAttachmentModalOpen(true));
        setTimeout(() => {
          if (editableRef && editableRef?.current) editableRef.current.innerHTML = '';
        }, 10);
      }
      return false;
    },
    showUploadList: false,
    multiple: true,
    maxCount: 10,
  };

  const handleStartReached = async () => {
    if (!chatInfo || !chatData[chatInfo.id]) return;
    const currentChatData = chatData[chatInfo.id];
      
    if (
      currentChatData.isLoadedHistory &&
      currentChatData.hasMore &&
      !currnetChatLoading
    ) {
    const res = await dispatch(
      fetchChatHistory({
        chat_id: chatInfo.id,
        room_id: chatInfo.room_id,
        page: (currentChatData.currentPage || 1) + 1,
        append: true,
      })).unwrap();   
      setFirstItemIndex(prev => prev - res.history.length); 
    }
  };
  
  return (
    <>
      {chatInfo ? (
        <div
          className={cn(classes.messanger, {
            [classes.aiWrap]: aiAssistant
          })}
        >
          {!aiAssistant && (
            <div role='navigation' className={classes.header}>
              <div className={classes.center}>
                {!embedded && <div className={classes.name}>
                  {t(`Chats.types.${chatInfo?.entity_type}`)} {chatInfo?.entity_name}
                  {isSupportChat && SUPPORT_PHONE && <span className={classes.phone}>{SUPPORT_PHONE}</span>}
                </div>}
                <span className={classes.date}>
                  {getDeclension(chatData[chatInfo.id]?.totalMessages || 0 ,titleNames)}
                </span>
              </div>
              <Button
                type='text'
                icon={<ChatSettingIcon />}
                onClick={openRightSideBar}
                className={classes.btn}
              />
            </div>
          )}
          {Boolean(chatGroups) && chatGroups?.length > 0 && chatGroups[0].length > 0 ? (
            <GroupedVirtuoso
              overscan={1000}
              increaseViewportBy={{
                top: 800,
                bottom: 200,
              }}
              atTopThreshold={600}
              firstItemIndex={firstItemIndex}
              startReached={handleStartReached}
              followOutput
              ref={bodyRef}
              className={cn(
                classes.body,
                chatInfo.deleted_at ? classes.withoutForm : classes.withForm,
                getTheme() === 'dark' ? classes.bgImagedark : classes.bgImagelight,
              )}
              initialTopMostItemIndex={ flattedGroups.length - 1}
              components={{
                Header: () => chatData[chatInfo.id]?.isLoadedHistory &&
                chatData[chatInfo.id]?.hasMore &&
                !currnetChatLoading ? <Spinner /> : null,
                Footer: () => <div style={{ height: '1px', visibility: 'hidden' }} />
              }}
              groupCounts={groupCounts}
              groupContent={(index) => {
                const firstMessage = chatGroups[index][0];
                return firstMessage?.timestamp ? (
                  <div className={classes.dateSectionWrap}>
                    <div className={classes.dateSection}>
                      {getDateWithTimeZone(firstMessage.timestamp, `${userData?.date_format!}Chat`)}
                    </div>
                  </div>
                ) : <div style={{ height: '1px' }} />;
              }}
          
              itemContent={(flatIndex, messageIndex) => {
                // const currentIdx = flatIndex - runningTotal;
                // const isFirst = chatMessage?.user_name !== chatGroups[messageIndex][currentIdx - 1]?.user_name;

                let runningTotal = 0;
                // Вычисляем, в какой группе находится `flatIndex`
                for (let i = 0; i < groupCounts.length; i++) {
                  if (flatIndex < runningTotal + groupCounts[i]) {
                    break;
                  }
                  runningTotal += groupCounts[i];
                }
                
                const chatMessage = flattedGroups[flatIndex - firstItemIndex];
                if (!chatMessage) {
                  return <div style={{ height: '1px' }} />;
                }
                const isSender = chatMessage?.email === userData?.email;
                const isAIthinking = (flattedGroups.length - 1) === (flatIndex - firstItemIndex)
                  && chatMessage?.email === userData?.email
                  && chatMessage.status === 'sent' && aiAssistant;

                let messageToShow: any;

                if (isSender) {
                  messageToShow = (
                    <>
                      <SendMessage
                        key={`message-${chatMessage.created_at + messageIndex}-sender`}
                        chatMessage={chatMessage}
                        date={`${getDateWithTimeZone(chatMessage.created_at, `${userData?.date_format!}Time`)}`}
                        contextMenu={contextMenu}
                        isTagsDisabled={isTagsDisabled}
                        replyOn={replyOn}
                        onTagClick={onTagClick}
                        t={t}
                        currentUserEmail={userData?.email}
                        aiAssistant={aiAssistant}
                      />
                      {isAIthinking && <AwaitingMessage bodyRef={bodyRef}/>}
                    </>
                  )
                } else {
                  messageToShow = (
                    <RecieveMessage
                      key={`message-${chatMessage.created_at + messageIndex}-recieve`}
                      chatMessage={chatMessage}
                      date={`${getDateWithTimeZone(chatMessage.created_at, `${userData?.date_format!}Time`)}`}
                      contextMenu={contextMenu}
                      isTagsDisabled={isTagsDisabled}
                      replyOn={replyOn}
                      onTagClick={onTagClick}
                      t={t}
                      currentUserEmail={userData?.email}
                      replyHandle={replyHandle}
                      aiAssistant={aiAssistant}
                    />
                  )
                }
                return messageToShow;
              }}
            />
          ) : (
            <div className={cn(
              classes.body,
              getTheme() === 'dark' ? classes.bgImagedark : classes.bgImagelight,
            )}>
              <div className={classes.newChatLabel}>
                <MessageOutlined className={classes.newChatIcon} />
                {!aiAssistant && (
                  <div className={classes.newChatText}>
                    
                  </div>
                )}
                <div className={classes.newChatSubtext}>
                  {t('Chats.newChatSubtext')}
                </div>
              </div>
            </div>
          )}

          {!chatInfo.deleted_at && <Form onFinish={onFinish} form={form}>
            {messageForReply && (
              <div className={classes.replyBlock}>
                <div className={classes.replyWrap}>
                  <div className={classes.whom}>
                    {t('Chats.inReply')} {messageForReply?.author?.name}
                  </div>
                  {Boolean(messageForReply?.attachment_ids?.length) && (
                    <div className={classes.repliedImages}>
                      {messageForReply?.attachment_ids?.slice(0, 3).map((id, index) => (
                        <Image
                          key={`reply-form-image-${index}`}
                          src={id}
                          fallback={fallbackHolder}
                          alt='Attachment'
                          className={classes.repliedImage}
                          preview={false}
                        />
                      ))}
                      {(messageForReply?.attachment_ids?.length || 0) > 3 && (
                        <div className={classes.repliedImageCount}>
                          +{(messageForReply?.attachment_ids?.length || 0) - 3}
                        </div>
                      )}
                    </div>
                  )}
                  {messageForReply?.content && (
                    <div>{parsedChatMessages(
                      messageForReply?.content,
                      { className: classes.messageTag, disabled: isTagsDisabled, onClick: onTagClick },
                      isTagsDisabled ? t('Documents.table.noPermissionToView') : undefined,
                      classes.urlLink,
                      classes.userMessageTag
                    )}</div>
                  )}
                </div>
                <div className={classes.replyClose}>
                  <CloseOutlined onClick={() => dispatch(setMessageForReply(null))} />
                </div>
              </div>
            )}
            <div className={cn(classes.footer, !messageForReply ? classes.borderTop : null)}>
              <Form.Item
                name='message'
                className={classes.item}
              >
                <CustomInput
                  tagsDocuments={tagsDocuments}
                  onFinish={onFinish}
                  onOpenAttachment={onOpenAttachment}
                  setAttachment={setAttachment}
                  form={form}
                  editableRef={editableRef}
                  needToShowMenus={!isAttachmentModalOpen}
                  aiAssistant={aiAssistant}
                  isSendingDisabled={isSendingDisabled}
                />
              </Form.Item>

              {!aiAssistant && (
                <CustomDragger uploadProps={uploadProps}/>
              )}

            </div>
          </Form>}
        </div>
      ) : (
        <Empty description={t('Chats.unselectedState')} className={classes.empty} />
      )}

      {isLoadingTagFile && <UploadingComponent />}

      {isAttachmentModalOpen && (
        <React.Suspense fallback={<div />}>
          <AttachmentModal
            attachment={attachment!}
            setAttachment={setAttachment}
            onClose={onCloseAttachment}
            onFinish={onFinish}
            onOpenAttachment={onOpenAttachment}
            tagsDocuments={tagsDocuments}
          />
        </React.Suspense>
      )}
    </>
  );
};

export default Messanger;
